<template>
  <div class="event-detail-page">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p class="loading-text">Loading event details...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="!event" class="error-container">
      <div class="empty-state">
        <h2>Event Not Found</h2>
        <p>This event might have been moved or is no longer available. Let's explore other exciting events! 🎉</p>
        <router-link to="/events" class="retry-btn">Back to Events</router-link>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else class="event-content">
      <!-- Back Button -->
      <div class="container">
        <router-link to="/events" class="back-btn">
          <i class="fa fa-arrow-left"></i>
          Back to Events
        </router-link>
      </div>

      <!-- Hero Section -->
      <div class="hero-section">
        <div class="container">
          <div class="hero-content">
            <div class="event-category-badge">
              {{ event.category || 'Event' }}
            </div>
            <h1 class="hero-title">{{ event.title }}</h1>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="container main-content">
        <!-- Video Section -->
        <div v-if="event.video" class="video-section">
          <h2>Event Video</h2>
          <div class="video-container" @click="openVideo">
            <img :src="event.image || event.thumbnail" :alt="event.title" class="video-thumbnail" />
            <div class="video-overlay">
              <i class="fa fa-play-circle play-icon"></i>
            </div>
          </div>
        </div>

        <!-- Event Data Section -->
        <div class="event-data-section">
          <div class="event-meta">
            <span class="meta-item">
              <i class="fa fa-calendar"></i>
              {{ formatDate(event.date) }}
            </span>
            <span class="meta-item">
              <i class="fa fa-clock"></i>
              {{ formatTime(event.date) }}
            </span>
            <span class="meta-item">
              <i class="fa fa-map-marker-alt"></i>
              {{ event.location }}
            </span>
            <span v-if="event.capacity" class="meta-item">
              <i class="fa fa-users"></i>
              {{ event.capacity }} Seats
            </span>
          </div>
        </div>

        <!-- Event Details -->
        <div class="details-section">
          <h2>Event Details</h2>
          <div class="details-content">
            <div class="details-grid">
              <div class="detail-item">
                <h3><i class="fa fa-info-circle"></i> About This Event</h3>
                <p>{{ event.long_description || event.description }}</p>
              </div>
              
              <div v-if="event.agenda" class="detail-item">
                <h3><i class="fa fa-list"></i> Event Agenda</h3>
                <ul>
                  <li v-for="(item, index) in event.agenda" :key="index">
                    <span class="bullet">•</span>
                    <span>{{ item }}</span>
                  </li>
                </ul>
              </div>
              
              <div v-if="event.speakers" class="detail-item">
                <h3><i class="fa fa-user-tie"></i> Speakers</h3>
                <div class="speakers-grid">
                  <div v-for="speaker in event.speakers" :key="speaker.id" class="speaker-card">
                    <img :src="speaker.avatar" :alt="speaker.name" class="speaker-avatar" />
                    <div class="speaker-info">
                      <h4>{{ speaker.name }}</h4>
                      <p>{{ speaker.title }}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="detail-item">
                <h3><i class="fa fa-map-marker-alt"></i> Venue Information</h3>
                <p>{{ event.venue_details ||event.location || 'Venue details will be shared upon registration.' }}</p>
              </div>
              
              <div v-if="event.price" class="detail-item">
                <h3><i class="fa fa-tag"></i> Pricing</h3>
                <p>{{ event.price }}</p>
              </div>
              
              <div v-if="event.contact" class="detail-item">
                <h3><i class="fa fa-phone"></i> Contact Information</h3>
                <p>{{ event.contact }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Event Schedule -->
        <div v-if="event.schedule" class="schedule-section">
          <h2>Event Schedule</h2>
          <div class="schedule-content">
            <div class="timeline">
              <div v-for="(session, index) in event.schedule" :key="index" class="timeline-item">
                <div class="timeline-time">{{ session.time }}</div>
                <div class="timeline-content">
                  <h4>{{ session.title }}</h4>
                  <p>{{ session.description }}</p>
                  <span v-if="session.speaker" class="speaker-name">{{ session.speaker }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Video Modal -->
    <div v-if="showVideoModal" class="modal-overlay" @click.self="closeVideo">
      <div class="modal-header-bar">
        <span class="modal-title">{{ event.title }}</span>
        <button class="modal-close" @click="closeVideo">Close &times;</button>
      </div>
      <div class="modal-video-center">
        <div class="video-wrapper">
          <iframe
            v-if="videoEmbedUrl"
            :key="'iframe-' + videoKey"
            :src="videoEmbedUrl"
            frameborder="0"
            allow="autoplay; encrypted-media"
            allowfullscreen
            class="modal-video"
          ></iframe>
          <video
            v-else-if="isDirectVideo"
            :key="'video-' + videoKey"
            :src="event.video"
            controls
            autoplay
            class="modal-video"
          ></video>
          <div v-else class="text-center py-5" style="color: #fff;">Video format not supported.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useEventStore } from '../stores/eventStore'

const route = useRoute()
const eventStore = useEventStore()
const event = ref(null)
const loading = ref(true)
const showVideoModal = ref(false)
const videoKey = ref(0)

// Computed properties
const isYouTube = computed(() => {
  return event.value?.video && /youtube\.com|youtu\.be/.test(event.value.video)
})

const isVimeo = computed(() => {
  return event.value?.video && /vimeo\.com/.test(event.value.video)
})

const isDirectVideo = computed(() => {
  return event.value?.video && !isYouTube.value && !isVimeo.value
})

const videoEmbedUrl = computed(() => {
  if (!event.value?.video) return ''
  if (isYouTube.value) {
    const match = event.value.video.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([\w-]{11})/)
    const id = match ? match[1] : ''
    return id ? `https://www.youtube.com/embed/${id}?autoplay=1` : ''
  }
  if (isVimeo.value) {
    const match = event.value.video.match(/vimeo\.com\/(\d+)/)
    const id = match ? match[1] : ''
    return id ? `https://player.vimeo.com/video/${id}?autoplay=1` : ''
  }
  return ''
})

// Methods
function formatDate(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

function formatTime(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  })
}

function openVideo() {
  if (event.value?.video) {
    videoKey.value++
    showVideoModal.value = true
  }
}

function closeVideo() {
  showVideoModal.value = false
}

onMounted(async () => {
  try {
    if (!eventStore.events.length) {
      await eventStore.fetchEvents()
    }
    event.value = eventStore.events.find(e => String(e.id) === String(route.params.id))
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.event-detail-page {
  min-height: 100vh;
  background: #f8fafc;
}

/* Loading State */
.loading-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 2px solid #e5e7eb;
  border-top: 2px solid #ff6b35;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  font-size: 18px;
  color: #6b7280;
}

/* Error State */
.error-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-state {
  text-align: center;
  max-width: 400px;
  padding: 40px 20px;
}

.empty-state h2 {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 12px;
}

.empty-state p {
  color: #6b7280;
  margin-bottom: 24px;
  line-height: 1.6;
}

.retry-btn {
  display: inline-block;
  background: #ff6b35;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: background 0.2s;
}

.retry-btn:hover {
  background: #e55a2b;
}

/* Container */
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
}

/* Back Button */
.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  color: #6b7280;
  text-decoration: none;
  font-weight: 500;
  margin: 24px 0;
  transition: color 0.2s;
}

.back-btn:hover {
  color: #ff6b35;
}

/* Hero Section */
.hero-section {
  background: linear-gradient(135deg, #ff6b35 0%, #e55a2b 100%);
  color: white;
  padding: 80px 0;
}

.hero-content {
  text-align: center;
}

.event-category-badge {
  background: rgba(255, 255, 255, 0.95);
  color: #ff6b35;
  font-size: 14px;
  font-weight: 700;
  padding: 8px 16px;
  border-radius: 25px;
  display: inline-block;
  margin-bottom: 24px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.hero-title {
  font-size: 56px;
  font-weight: 900;
  margin-bottom: 24px;
  line-height: 1.1;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  letter-spacing: -0.5px;
}

/* Event Data Section */
.event-data-section {
  margin-bottom: 60px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  padding: 40px;
  text-align: center;
  margin-top: 40px;
}

.event-data-section .event-meta {
  display: flex;
  justify-content: center;
  gap: 32px;
  flex-wrap: wrap;
  margin-bottom: 32px;
}

.event-data-section .event-meta .meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  color: #374151;
  font-weight: 600;
  padding: 12px 20px;
  background: #f8fafc;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.event-data-section .event-meta .meta-item:hover {
  background: #ff6b35;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(255, 107, 53, 0.3);
}

.event-data-section .event-meta .meta-item i {
  font-size: 16px;
  color: #ff6b35;
  transition: color 0.3s ease;
}

.event-data-section .event-meta .meta-item:hover i {
  color: white;
}

.event-data-section .event-description p {
  color: #374151;
  font-size: 18px;
  line-height: 1.8;
  max-width: 800px;
  margin: 0 auto;
  font-weight: 500;
}

/* Main Content */
.main-content {
  padding: 48px 0;
}

/* Video Section */
.video-section {
  margin-bottom: 60px;
  text-align: center;
}

.video-section h2 {
  font-size: 32px;
  font-weight: 800;
  color: #111827;
  margin-bottom: 40px;
  text-align: center;
  letter-spacing: -0.5px;
}

.video-container {
  position: relative;
  cursor: pointer;
  max-width: 800px;
  margin: 0 auto;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;
}

.video-container:hover {
  transform: translateY(-5px);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);
}

.video-thumbnail {
  width: 100%;
  height: 450px;
  object-fit: cover;
}

.video-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s ease;
}

.video-container:hover .video-overlay {
  background: rgba(0, 0, 0, 0.6);
}

.play-icon {
  font-size: 100px;
  color: white;
  opacity: 0.9;
  transition: transform 0.3s ease;
}

.video-container:hover .play-icon {
  transform: scale(1.1);
}

/* Details Section */
.details-section {
  margin-bottom: 48px;
}

.details-section h2 {
  font-size: 32px;
  font-weight: 800;
  color: #111827;
  margin-bottom: 32px;
  text-align: center;
  letter-spacing: -0.5px;
}

.details-content {
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  padding: 40px;
}

.details-grid {
  display: grid;
  gap: 40px;
}

.detail-item h3 {
  font-size: 24px;
  font-weight: 800;
  color: #111827;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  letter-spacing: -0.3px;
}

.detail-item h3 i {
  color: #ff6b35;
  font-size: 20px;
}

.detail-item p {
  color: #374151;
  line-height: 1.8;
  font-size: 18px;
  font-weight: 500;
}

.detail-item ul {
  list-style: none;
  padding: 0;
}

.detail-item li {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
  color: #374151;
  font-size: 18px;
  line-height: 1.7;
  font-weight: 500;
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.detail-item li:hover {
  background: #ff6b35;
  color: white;
  transform: translateX(5px);
}

.detail-item .bullet {
  color: #ff6b35;
  margin-right: 12px;
  font-weight: 700;
  font-size: 20px;
  transition: color 0.3s ease;
}

.detail-item li:hover .bullet {
  color: white;
}

/* Speakers Grid */
.speakers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-top: 20px;
}

.speaker-card {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.speaker-card:hover {
  transform: translateY(-5px);
  background: white;
  border-color: #ff6b35;
  box-shadow: 0 12px 30px rgba(255, 107, 53, 0.15);
}

.speaker-avatar {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #ff6b35;
}

.speaker-info h4 {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 6px 0;
  letter-spacing: -0.2px;
}

.speaker-info p {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
  font-weight: 500;
}

/* Schedule Section */
.schedule-section {
  margin-bottom: 48px;
}

.schedule-section h2 {
  font-size: 32px;
  font-weight: 800;
  color: #111827;
  margin-bottom: 32px;
  text-align: center;
  letter-spacing: -0.5px;
}

.schedule-content {
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  padding: 40px;
}

.timeline {
  position: relative;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 24px;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(to bottom, #ff6b35, #e55a2b);
  border-radius: 2px;
}

.timeline-item {
  display: flex;
  margin-bottom: 40px;
  position: relative;
  transition: transform 0.3s ease;
}

.timeline-item:hover {
  transform: translateX(5px);
}

.timeline-time {
  width: 120px;
  font-weight: 700;
  color: #ff6b35;
  font-size: 16px;
  margin-right: 32px;
  flex-shrink: 0;
  text-align: right;
}

.timeline-content {
  flex: 1;
  padding-left: 32px;
  position: relative;
}

.timeline-content::before {
  content: '';
  position: absolute;
  left: -8px;
  top: 12px;
  width: 16px;
  height: 16px;
  background: #ff6b35;
  border-radius: 50%;
  border: 3px solid white;
  box-shadow: 0 0 0 3px #ff6b35;
}

.timeline-content h4 {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 12px 0;
  letter-spacing: -0.2px;
}

.timeline-content p {
  color: #6b7280;
  font-size: 18px;
  line-height: 1.7;
  margin: 0 0 12px 0;
  font-weight: 500;
}

.speaker-name {
  font-size: 16px;
  color: #ff6b35;
  font-weight: 600;
  display: inline-block;
  padding: 4px 12px;
  background: rgba(255, 107, 53, 0.1);
  border-radius: 20px;
}

/* Video Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-header-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
}

.modal-close {
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-video-center {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 20px 20px;
}

.video-wrapper {
  width: 100%;
  max-width: 800px;
  height: 100%;
  max-height: 600px;
}

.modal-video {
  width: 100%;
  height: 100%;
  border-radius: 8px;
}

/* Responsive */
@media (max-width: 768px) {
  .container {
    padding: 0 12px;
  }
  
  .hero-title {
    font-size: 32px;
  }
  
  .event-meta {
    flex-direction: column;
    gap: 12px;
  }
  
  .hero-section {
    padding: 40px 0;
  }
  
  .main-content {
    padding: 32px 0;
  }
  
  .video-thumbnail {
    height: 300px;
  }
  
  .timeline::before {
    left: 16px;
  }
  
  .timeline-time {
    width: 80px;
    font-size: 12px;
  }
  
  .timeline-content::before {
    left: -4px;
    width: 8px;
    height: 8px;
  }
  
  .details-content,
  .schedule-content {
    padding: 20px;
  }
  
  .speakers-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 28px;
  }
  
  .video-thumbnail {
    height: 250px;
  }
  
  .details-content,
  .schedule-content {
    padding: 16px;
  }
  
  .timeline-time {
    width: 60px;
    font-size: 11px;
  }
}
</style> 